import { statuses, EAP_REGIONS } from "../../constant/constants";
import { getStaticDataSourceSync } from "../../constant/staticDataSources";
import { isUnemployedEmploymentType } from "../../constant/utils/employmentTypeUtils";

const PROVINCE_FALLBACK = EAP_REGIONS.filter((r) => !r.isNational).map((r) => r.name);

/**
 * @param {Object} data - row data
 * @param {Array} seta_json - SETA template fields
 * @param {number} rowIndex
 * @param {boolean} isWorkflowReq
 * @param {Array} existingEmployees
 * @param {string|null} currentEmployeeId
 * @param {string|null} companyId
 * @param {boolean} isInvited
 * @param {Array} [employmentTypes] - optional; from file_structure.sheets[0].employmentTypes. When provided, unemployed rows only require first name; surname and other fields have no missing validation.
 * @param {string|null} [overrideEmploymentTypeValue] - optional; use when row employment type is from API (e.g. employee.employment_type) so unemployed is detected even if data lacks the field.
 */
export const validateMissingData = (
  data,
  seta_json,
  rowIndex = 1,
  isWorkflowReq = true,
  existingEmployees = [],
  currentEmployeeId = null,
  companyId = null,
  isInvited = true,
  employmentTypes = undefined,
  overrideEmploymentTypeValue = null
) => {
  const missingData = [];
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const employmentTypeField = seta_json.find((f) => f.config?.isEmploymentTypeField);
  const fromData = employmentTypeField && data[employmentTypeField.fieldName];
  const employmentTypeValue = (fromData != null && fromData !== "") ? fromData : (overrideEmploymentTypeValue ?? null);
  const typesForUnemployedCheck =
    employmentTypes?.length > 0
      ? employmentTypes
      : (Array.isArray(employmentTypeField?.allowedValues) && employmentTypeField.allowedValues.every((o) => o != null && typeof o === "object" && "isUnemployedType" in o))
        ? employmentTypeField.allowedValues
        : [];
  const isUnemployed = Boolean(
    typesForUnemployedCheck.length && isUnemployedEmploymentType(typesForUnemployedCheck, employmentTypeValue)
  );

  for (const field of seta_json) {
    // Skip if field is not for employee import
    if (!field.config.employeeImport) continue;

    if (isUnemployed) {
      // For unemployed: only require first name; surname/last name is optional (no missing validation).
      const key = field.fieldName;
      const isFirstNameField = field.config?.isFirstNameField;

      if (isFirstNameField) {
        const isMissing = !data[key] || data[key] === "" || data[key] === null || !String(data[key]).trim().length;
        if (isMissing) {
          missingData.push({
            row: `Row ${rowIndex}`,
            missing: key,
          });
        }
      }

      // Still enforce duplicate emp_id only when emp_id field has a non-empty value
      const empIdFieldUnemployed = seta_json.find((f) => f.config?.isEmpId);
      const isEmpIdThisField = empIdFieldUnemployed && field.fieldName === empIdFieldUnemployed.fieldName;
      const empIdVal = isEmpIdThisField && data[key] != null ? String(data[key]).trim() : "";
      if (isEmpIdThisField && empIdVal !== "") {
        const isDuplicateEmpId = existingEmployees.some((emp) => {
          if (currentEmployeeId && emp.id === currentEmployeeId) return false;
          if (companyId && emp.company_id !== companyId) return false;
          const existingEmpId = String((emp.emp_id || (emp.data && emp.data[empIdFieldUnemployed.fieldName])) ?? "").trim();
          return existingEmpId !== "" && empIdVal === existingEmpId && emp.status !== statuses.ARCHIVED;
        });
        if (isDuplicateEmpId) {
          missingData.push({
            row: `Row ${rowIndex}`,
            duplicateEmpId: data[key],
          });
        }
      }
      const isEmailField = field.config?.validationType === "email" || field.config?.isEmailField;
      if (isEmailField && data[key] && !emailRegex.test(data[key])) {
        missingData.push({
          row: `Row ${rowIndex}`,
          key,
          value: data[key],
          invalidEmail: data[key],
        });
      }
      continue;
    }

    // Skip if field is not required based on workflow status
    if (isWorkflowReq && !field.config.isRequiredInWorkflow) continue;
    if (!isWorkflowReq && !field.config.isRequiredInWithoutWorkflow) continue;

    // Skip department field check if not in workflow
    if (!isWorkflowReq && field.config.isDepartmentNameField) continue;

    const key = field.fieldName;

    let allowedValues = field.allowedValues || [];
    if (field.staticDataSourceName) {
      const staticValues = getStaticDataSourceSync(field.staticDataSourceName);
      allowedValues = staticValues.length > 0 ? staticValues : (field.allowedValues || []);
      if (allowedValues.length === 0 && field.staticDataSourceName.toLowerCase().trim() === "province") {
        allowedValues = PROVINCE_FALLBACK;
      }
    }

    // Build accepted options from both value and label so Excel import (often using labels) validates
    const options = (allowedValues || []).flatMap((item) => {
      if (typeof item === "object" && item != null && ("value" in item || "label" in item)) {
        const normalized = [];
        if (item.value != null && item.value !== "") normalized.push(String(item.value).toLowerCase().trim());
        if (item.label != null && item.label !== "") normalized.push(String(item.label).toLowerCase().trim());
        return normalized;
      }
      const v = item != null ? String(item).toLowerCase().trim() : "";
      return v ? [v] : [];
    });

    const isMissing =
      !data[key] || data[key] === "" || data[key] === null || !String(data[key]).length;
    const enteredNormalized = String(data[key] || "").toLowerCase().trim();
    const isInvalidValue =
      allowedValues &&
      allowedValues.length > 0 &&
      data[key] != null &&
      enteredNormalized !== "" &&
      !options.includes(enteredNormalized);

    const isEmailField = field.config?.validationType === "email" || field.config?.isEmailField;
    const isInvalidEmail = isEmailField && data[key] && !emailRegex.test(data[key]);
    const isUninvitedEmail = isEmailField && !isInvited && isWorkflowReq;

    const empIdField = seta_json.find((f) => f.config?.isEmpId);
    const isEmpIdField = empIdField && field.fieldName === empIdField.fieldName;
    const empIdValue = isEmpIdField ? (data[key] != null ? String(data[key]).trim() : "") : "";
    const isDuplicateEmpId =
      isEmpIdField &&
      empIdValue !== "" &&
      existingEmployees.some((emp) => {
        if (currentEmployeeId && emp.id === currentEmployeeId) return false;
        if (companyId && emp.company_id !== companyId) return false;
        const rawEmpId = emp.emp_id || (emp.data && emp.data[empIdField.fieldName]);
        const existingEmpId = String(rawEmpId != null ? rawEmpId : "").trim();
        return existingEmpId !== "" && empIdValue === existingEmpId && emp.status !== statuses.ARCHIVED;
      });

    if (isMissing || isInvalidValue || isInvalidEmail || isUninvitedEmail || isDuplicateEmpId) {
      missingData.push({
        row: `Row ${rowIndex}`,
        missing: isMissing ? key : undefined,
        key: isInvalidValue ? key : undefined,
        value: isInvalidValue ? data[key] : undefined,
        allowed: isInvalidValue ? allowedValues : undefined,
        invalidEmail: isInvalidEmail ? data[key] : undefined,
        uninvitedEmail: isUninvitedEmail ? data[key] : undefined,
        duplicateEmpId: isDuplicateEmpId ? data[key] : undefined,
      });
    }
  }

  return missingData;
};

/** Human-readable line for one validateMissingData issue (Verify column / plan preflight). */
export function formatEmployeeDataIssueText(issue) {
  if (!issue || typeof issue !== "object") return "";
  if (issue.missing) return `${issue.missing} is missing`;
  if (issue.invalidEmail) return "Email address is not valid";
  if (issue.duplicateEmpId) return "Employee ID is already in use";
  if (issue.key && issue.value != null && issue.value !== "") {
    return `${issue.key} is not valid (entered: ${issue.value})`;
  }
  return "";
}

/**
 * Employees in Skills Planner scope (get_training_forms rows) with data issues.
 * Uses the same rules as Employee List Verify column.
 * @param {Array} trainingFormRows — RPC rows with empData
 * @param {Object} options
 * @returns {Array<{ empId: string, name: string, issues: object[] }>}
 */
export function collectPlanEmployeeDataIssues(trainingFormRows, options) {
  const {
    setaTemplateFields,
    isWorkflowReq = true,
    employmentTypes,
    companyId,
  } = options || {};

  if (!Array.isArray(trainingFormRows) || !Array.isArray(setaTemplateFields)) {
    return [];
  }

  const employeesInScope = trainingFormRows
    .map((row) => row?.empData)
    .filter((emp) => emp && emp.id);

  const byId = new Map();

  for (const emp of employeesInScope) {
    if (byId.has(emp.id)) continue;

    const issues = validateMissingData(
      emp.data || {},
      setaTemplateFields,
      1,
      isWorkflowReq,
      employeesInScope,
      emp.id,
      companyId,
      emp.invited,
      employmentTypes,
      emp.employment_type ?? null,
    ).filter(
      (item) =>
        item.missing ||
        (item.key && item.value != null) ||
        item.invalidEmail ||
        item.duplicateEmpId,
    );

    if (issues.length > 0) {
      const name = [emp.first_name, emp.last_name].filter(Boolean).join(" ");
      byId.set(emp.id, {
        id: emp.id,
        empId: emp.emp_id != null ? String(emp.emp_id) : "",
        name,
        issues,
      });
    }
  }

  return [...byId.values()];
}
